import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TableSortLabel,
  Toolbar,
  Button,
  MenuItem,
  TablePagination,
} from '@mui/material'

import { fetchWithAuth } from '../utils/fetchWithAuth'
import EditButton from '../components/EditButtonModal'

export default function AdminSubscriptions() {
  const [subscriptions, setSubscriptions] = useState([])
  const [membershipTypes, setMembershipTypes] = useState([])

  const [editOpen, setEditOpen] = useState(false)
  const [editing, setEditing] = useState(null)

  const [orderBy, setOrderBy] = useState('id')
  const [order, setOrder] = useState('asc')

  const [filterText, setFilterText] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const fetchSubscriptions = async () => {
    try {
      const res = await fetchWithAuth('/admin/subscriptions')

      if (!res.ok) {
        const err = await res.json().catch(() => ({}))

        console.error(
          'Chyba pri načítaní predplatného:',
          err.message || res.status
        )

        setSubscriptions([])
        return
      }

      const data = await res.json()

      setSubscriptions(
        Array.isArray(data)
          ? data
          : []
      )
    } catch (error) {
      console.error(
        'Chyba pri načítaní predplatného:',
        error
      )
    }
  }

  const fetchMembershipTypes = async () => {
    try {
      const res = await fetchWithAuth('/admin/membership-types')

      if (!res.ok) {
        setMembershipTypes([])
        return
      }

      const data = await res.json()

      setMembershipTypes(
        Array.isArray(data)
          ? data
          : []
      )
    } catch (error) {
      console.error(
        'Chyba pri načítaní typov predplatného:',
        error
      )

      setMembershipTypes([])
    }
  }

  useEffect(() => {
    fetchSubscriptions()
    fetchMembershipTypes()
  }, [])

  const columns = useMemo(
    () => [
      {
        id: 'id',
        label: 'ID',
        get: s => s.id
      },
      {
        id: 'userId',
        label: 'User ID',
        get: s => s.userId
      },
      {
        id: 'meno',
        label: 'Meno',
        get: s => s.user?.meno || ''
      },
      {
        id: 'priezvisko',
        label: 'Priezvisko',
        get: s => s.user?.priezvisko || ''
      },
      {
        id: 'email',
        label: 'Email',
        get: s => s.user?.email || ''
      },
      {
        id: 'membership_type_id',
        label: 'Typ ID',
        get: s => s.membership_type_id
      },
      {
        id: 'type',
        label: 'Predplatné',
        get: s =>
          s.membershipType?.type ||
          s.type ||
          ''
      },
      {
        id: 'valid_from',
        label: 'Platnosť od',
        get: s =>
          s.valid_from
            ? s.valid_from.split('T')[0]
            : ''
      },
      {
        id: 'valid_to',
        label: 'Platnosť do',
        get: s =>
          s.valid_to
            ? s.valid_to.split('T')[0]
            : ''
      },
      {
        id: 'createdAt',
        label: 'Vytvorené',
        get: s =>
          s.createdAt
            ? s.createdAt.split('T')[0]
            : ''
      },
      {
        id: 'updatedAt',
        label: 'Upravené',
        get: s =>
          s.updatedAt
            ? s.updatedAt.split('T')[0]
            : ''
      },
    ],
    []
  )

  const processedSubscriptions = useMemo(() => {
    const q =
      filterText
        .trim()
        .toLowerCase()

    const filtered = q
      ? subscriptions.filter(s => {
          const values = [
            s.id,
            s.userId,
            s.user?.meno,
            s.user?.priezvisko,
            s.user?.email,
            s.membership_type_id,
            s.membershipType?.type,
            s.type,
            s.valid_from,
            s.valid_to,
            s.createdAt,
            s.updatedAt,
          ]
            .filter(v => v !== null && v !== undefined)
            .join(' ')
            .toLowerCase()

          return values.includes(q)
        })
      : subscriptions

    const col =
      columns.find(c => c.id === orderBy) ||
      columns[0]

    const rows = filtered.map(s => ({
      s,
      v: col.get(s)
    }))

    rows.sort((a, b) => {
      const av = a.v ?? ''
      const bv = b.v ?? ''

      const an =
        typeof av === 'number'
          ? av
          : Number.isFinite(+av) && av !== ''
            ? +av
            : null

      const bn =
        typeof bv === 'number'
          ? bv
          : Number.isFinite(+bv) && bv !== ''
            ? +bv
            : null

      let cmp = 0

      if (an !== null && bn !== null) {
        cmp = an - bn
      } else {
        cmp = String(av).localeCompare(
          String(bv),
          'sk',
          {
            sensitivity: 'base',
            numeric: true
          }
        )
      }

      return order === 'asc'
        ? cmp
        : -cmp
    })

    return rows.map(x => x.s)
  }, [
    subscriptions,
    filterText,
    order,
    orderBy,
    columns
  ])

  const paginatedSubscriptions =
    processedSubscriptions.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    )

  const handleSort = id => {
    if (orderBy === id) {
      setOrder(prev =>
        prev === 'asc'
          ? 'desc'
          : 'asc'
      )
    } else {
      setOrderBy(id)
      setOrder('asc')
    }
  }

  const toDateValue = value => {
    if (!value) return ''

    return String(value).split('T')[0]
  }

  const openEdit = subscription => {
    setEditing({
      id: subscription.id,
      userId: subscription.userId,
      meno: subscription.user?.meno || '',
      priezvisko: subscription.user?.priezvisko || '',
      email: subscription.user?.email || '',
      membership_type_id:
        subscription.membership_type_id ?? '',
      valid_from:
        toDateValue(subscription.valid_from),
      valid_to:
        toDateValue(subscription.valid_to),
    })

    setEditOpen(true)
  }

  const closeEdit = () => {
    setEditOpen(false)
    setEditing(null)
  }

  const saveEdit = async () => {
    if (!editing) return

    const res = await fetchWithAuth(
      `/admin/subscriptions/${editing.id}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          membership_type_id:
            Number(editing.membership_type_id),
          valid_from: editing.valid_from,
          valid_to: editing.valid_to
        })
      }
    )

    if (!res.ok) {
      const err =
        await res.json().catch(() => ({}))

      alert(
        err?.detail ||
        err?.message ||
        'Chyba pri ukladaní'
      )

      return
    }

    closeEdit()
    await fetchSubscriptions()
  }

  return (
    <div
      style={{
        background: '#ededed',
        padding: 12,
        border: '1.5px solid #107c6b',
        borderRadius: 10,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          marginBottom: 1
        }}
      >
        <h3
          style={{
            fontSize: 16,
            margin: 0
          }}
        >
          Zoznam predplatného
        </h3>

        <Typography
          sx={{
            fontSize: 13,
            opacity: 0.7
          }}
        >
          Záznamov: {processedSubscriptions.length}
        </Typography>
      </Box>

      <Toolbar
        disableGutters
        sx={{
          minHeight: '42px !important',
          marginBottom: 1
        }}
      >
        <TextField
          value={filterText}
          onChange={e => {
            setFilterText(e.target.value)
            setPage(0)
          }}
          size="small"
          label="Filtrovať (full-text)"
          placeholder="Hľadať podľa mena, emailu, predplatného..."
          sx={{
            width: 400,
            maxWidth: '100%',
            '& .MuiInputBase-root': {
              fontSize: 13
            },
            '& .MuiInputLabel-root': {
              fontSize: 13
            }
          }}
        />
      </Toolbar>

      <TableContainer
        component={Paper}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          border: '1px solid #c7c7c7',
          borderRadius: '10px',
          boxShadow: 'none'
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: 1000,

            '& thead th': {
              fontWeight: 'bold',
              backgroundColor: '#c8e1e1',
              whiteSpace: 'nowrap',
              fontSize: 12,
              padding: '6px 8px'
            },

            '& tbody td': {
              fontSize: 12,
              padding: '5px 8px',
              whiteSpace: 'nowrap'
            },

            '& .MuiTableSortLabel-root': {
              fontSize: 12
            }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>
                Upraviť
              </TableCell>

              {columns.map(col => (
                <TableCell
                  key={col.id}
                  sortDirection={
                    orderBy === col.id
                      ? order
                      : false
                  }
                >
                  <TableSortLabel
                    active={
                      orderBy === col.id
                    }
                    direction={
                      orderBy === col.id
                        ? order
                        : 'asc'
                    }
                    onClick={() =>
                      handleSort(col.id)
                    }
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedSubscriptions.map(s => (
              <TableRow
                key={s.id}
                hover
              >
                <TableCell>
                  <EditButton
                    onClick={() =>
                      openEdit(s)
                    }
                  />
                </TableCell>

                {columns.map(col => (
                  <TableCell key={col.id}>
                    {String(
                      col.get(s) ?? ''
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        <TablePagination
          component="div"
          count={processedSubscriptions.length}
          page={page}
          onPageChange={(_e, p) =>
            setPage(p)
          }
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(
              parseInt(
                e.target.value,
                10
              )
            )

            setPage(0)
          }}
          rowsPerPageOptions={[
            10,
            25,
            50,
            100
          ]}
          labelRowsPerPage="Na stránku"
        />
      </Box>

      <Dialog
        open={editOpen}
        onClose={closeEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upraviť predplatné
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <TextField
            label="ID"
            value={editing?.id ?? ''}
            disabled
            size="small"
          />

          <TextField
            label="User ID"
            value={editing?.userId ?? ''}
            disabled
            size="small"
          />

          <TextField
            label="Meno"
            value={
              `${editing?.meno || ''} ${editing?.priezvisko || ''}`.trim()
            }
            disabled
            size="small"
          />

          <TextField
            label="Email"
            value={editing?.email ?? ''}
            disabled
            size="small"
          />

          <TextField
            select
            label="Predplatné"
            value={
              editing?.membership_type_id ?? ''
            }
            onChange={e =>
              setEditing(v => ({
                ...v,
                membership_type_id:
                  e.target.value
              }))
            }
            size="small"
          >
            {membershipTypes.map(type => (
              <MenuItem
                key={type.id}
                value={type.id}
              >
                {type.type}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Platnosť od"
            type="date"
            value={
              editing?.valid_from ?? ''
            }
            onChange={e =>
              setEditing(v => ({
                ...v,
                valid_from:
                  e.target.value
              }))
            }
            InputLabelProps={{
              shrink: true
            }}
            size="small"
          />

          <TextField
            label="Platnosť do"
            type="date"
            value={
              editing?.valid_to ?? ''
            }
            onChange={e =>
              setEditing(v => ({
                ...v,
                valid_to:
                  e.target.value
              }))
            }
            InputLabelProps={{
              shrink: true
            }}
            size="small"
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeEdit}
          >
            Zrušiť
          </Button>

          <Button
            onClick={saveEdit}
            variant="contained"
          >
            Uložiť
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}